// src/modules/app-views/_apps/appmulti/plans/pages/Init/components/PlansPreviewActions/index.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { Button, Divider, Dropdown, Space } from 'antd';
import { ContainerOutlined, EditOutlined, MoreOutlined, RestOutlined } from '@ant-design/icons';
import { PiLifebuoy } from 'react-icons/pi';
import translateX from 'utils/translateX';

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const PlansPreviewActions = props => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const {
    selectedDocument,
    handleEditDocument,
    handleArchiveDocuments,
    handleRemoveDocuments,
    handleRestoreDocuments,
    setShow,
    folder,
    refresh,
  } = props;

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES
  // ─────────────────────────────────────────────────────────────────────────────
  if (!selectedDocument) return null;

  const isArchiveOrRecycle = folder === 'archive' || folder === 'recycle';
  const isRecycle = folder === 'recycle';

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const handleClose = () => setShow?.(false);

  const handleOpen = () => {
    handleClose();
    setTimeout(() => {
      handleEditDocument?.(selectedDocument);
    }, 100);
  };

  const handleArchive = () => {
    handleArchiveDocuments?.([selectedDocument], {
      onSuccess: () => {
        handleClose();
        refresh?.();
      },
    });
  };

  const handleRemove = () => {
    handleRemoveDocuments?.([selectedDocument], {
      onSuccess: () => {
        handleClose();
        refresh?.();
      },
    });
  };

  const handleRestore = () => {
    handleRestoreDocuments?.([selectedDocument], {
      onSuccess: () => {
        handleClose();
        refresh?.();
      },
    });
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. BUILD ACTIONS
  // ─────────────────────────────────────────────────────────────────────────────
  const primaryActions = [];
  const secondaryActions = [];

  if (isArchiveOrRecycle) {
    primaryActions.push({
      key: 'restore',
      label: translateX('restore'),
      icon: <PiLifebuoy />,
      onClick: handleRestore,
    });
    primaryActions.push({
      key: 'remove',
      label: isRecycle ? translateX('permanently_remove') : translateX('remove'),
      icon: <RestOutlined />,
      onClick: handleRemove,
      danger: isRecycle,
    });
  } else {
    primaryActions.push({
      key: 'open',
      label: translateX('open'),
      icon: <EditOutlined />,
      onClick: handleOpen,
      type: 'primary',
    });
    secondaryActions.push(
      {
        key: 'archive',
        label: translateX('archive'),
        icon: <ContainerOutlined />,
        onClick: handleArchive,
      },
      {
        key: 'remove',
        label: translateX('remove'),
        icon: <RestOutlined />,
        onClick: handleRemove,
        danger: true,
      }
    );
  }

  const menuItems = secondaryActions.map(action => ({
    key: action.key,
    icon: action.icon,
    label: action.label,
    danger: action.danger,
    onClick: action.onClick,
  }));

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <div>
      <Divider style={{ margin: '16px 0' }} />

      <Space direction="vertical" size={8} style={{ width: '100%' }}>
        {primaryActions.map(action => (
          <Button
            key={action.key}
            type={action.type || 'default'}
            icon={action.icon}
            onClick={action.onClick}
            danger={action.danger}
            block
          >
            {action.label}
          </Button>
        ))}

        {menuItems.length > 0 && (
          <Dropdown menu={{ items: menuItems }} trigger={['click']}>
            <Button icon={<MoreOutlined />} block>
              {translateX('more_actions')}
            </Button>
          </Dropdown>
        )}
      </Space>
    </div>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default PlansPreviewActions;
